import { DateTime24 } from "./DateTime24";
import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { createImpound, quoteImpound, fetchImpoundConfig } from "../lib/api";
import { toast } from "sonner";
import AddressAutocomplete from "./AddressAutocomplete";
import { toTenantWall as toLocalInput, wallToIso } from "../lib/tenantTime";
import { tenantLocale, tenantTz } from "../lib/company";


const fmtMoney = (n) =>
  n == null ? "—" : `$${Number(n).toLocaleString(tenantLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function NewImpoundDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(() => ({
    customer_name: "", customer_phone: "", vehicle: "", rego: "",
    address: "", lat: null, lng: null,
    drop_off_address: "", drop_off_lat: null, drop_off_lng: null,
    gvm_kg: "", impound_term: "28d",
    impounded_at: toLocalInput(new Date()),
    impound_notice_number: "", tow_authority_number: "",
    impound_owner_name: "", impound_owner_phone: "", impound_owner_address: "",
    is_public_holiday: false,
    already_completed: null,
    notes: "",
  }));
  const [quote, setQuote] = useState(null);
  const [saving, setSaving] = useState(false);
  const [yards, setYards] = useState([]);  // Loaded from config — admin-configurable
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Pull yards from the integration config when the dialog opens, so the
  // drop-off dropdown reflects whatever the admin has set under Integrations.
  useEffect(() => {
    if (!open) return;
    fetchImpoundConfig()
      .then((cfg) => {
        const ys = (cfg.yards || []).filter((y) => y && (y.address || "").trim());
        setYards(ys);
        // Default to first yard if nothing picked yet.
        if (ys.length && !form.drop_off_address) {
          setForm((f) => ({ ...f, drop_off_address: ys[0].address }));
        }
      })
      .catch(() => setYards([]));
  }, [open]);

  // Reset on open.
  useEffect(() => {
    if (open) {
      setForm({
        customer_name: "", customer_phone: "", vehicle: "", rego: "",
        address: "", lat: null, lng: null,
        drop_off_address: "", drop_off_lat: null, drop_off_lng: null,
        gvm_kg: "", impound_term: "28d",
        impounded_at: toLocalInput(new Date()),
        impound_notice_number: "", tow_authority_number: "",
        impound_owner_name: "", impound_owner_phone: "", impound_owner_address: "",
        is_public_holiday: false, already_completed: null, notes: "",
      });
      setQuote(null);
    }
  }, [open]);

  // Live quote whenever the pricing inputs change.
  const canQuote = useMemo(
    () => form.gvm_kg && Number(form.gvm_kg) > 0 && form.impound_term && form.impounded_at,
    [form.gvm_kg, form.impound_term, form.impounded_at]
  );
  useEffect(() => {
    if (!open || !canQuote) { setQuote(null); return; }
    let alive = true;
    const id = setTimeout(async () => {
      try {
        const q = await quoteImpound({
          gvm_kg: Number(form.gvm_kg),
          impounded_at: wallToIso(form.impounded_at),
          term: form.impound_term,
          base_to_base_km: null,
          days_overstay: 0,
          is_public_holiday: form.is_public_holiday || null,
        });
        if (alive) setQuote(q);
      } catch { /* keep stale quote */ }
    }, 300);
    return () => { alive = false; clearTimeout(id); };
  }, [open, canQuote, form.gvm_kg, form.impound_term, form.impounded_at, form.is_public_holiday]);

  const submit = async () => {
    if (!form.customer_name || !form.vehicle || !form.address || !form.gvm_kg) {
      toast.error("Driver name, vehicle, impound location and GVM are required.");
      return;
    }
    if (form.already_completed == null) {
      toast.error("Please answer: has the impound already been completed outside dispatcher hours?");
      return;
    }
    setSaving(true);
    try {
      const created = await createImpound({
        ...form,
        // Mirror the unified "Driver" identity into both legacy customer_* and impound_owner_* fields.
        impound_owner_name: form.customer_name,
        impound_owner_phone: form.customer_phone,
        impound_owner_address: form.impound_owner_address,
        gvm_kg: Number(form.gvm_kg),
        impounded_at: wallToIso(form.impounded_at),
      });
      toast.success("Impound job created");
      onOpenChange(false);
      onCreated?.(created);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create impound");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-impound-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-black tracking-tight">New Police Impound</DialogTitle>
          <DialogDescription>All fields with * required. Pricing auto-calculates from settings.</DialogDescription>
        </DialogHeader>

        {/* Dispatch question — decides whether this creates a queued job. */}
        <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 p-3 mt-2" data-testid="imp-already-completed-box">
          <div className="text-xs font-bold text-amber-900 dark:text-amber-200 mb-2">
            Has this impound already been completed outside dispatcher hours? *
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant={form.already_completed === true ? "default" : "outline"}
              className={form.already_completed === true ? "bg-slate-900 hover:bg-slate-800 text-white border border-slate-900 dark:bg-slate-100 dark:hover:bg-slate-200 dark:text-slate-900 dark:border-slate-100 dark:hover:border-slate-100 shadow-sm ring-2 ring-slate-900/20 dark:ring-slate-100/30" : ""}
              onClick={() => upd("already_completed", true)}
              data-testid="imp-already-completed-yes"
            >
              Yes — already done
            </Button>
            <Button
              type="button"
              size="sm"
              variant={form.already_completed === false ? "default" : "outline"}
              className={form.already_completed === false ? "bg-slate-900 hover:bg-slate-800 text-white border border-slate-900 dark:bg-slate-100 dark:hover:bg-slate-200 dark:text-slate-900 dark:border-slate-100 dark:hover:border-slate-100 shadow-sm ring-2 ring-slate-900/20 dark:ring-slate-100/30" : ""}
              onClick={() => upd("already_completed", false)}
              data-testid="imp-already-completed-no"
            >
              No — needs dispatch
            </Button>
          </div>
          <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1.5">
            Yes → goes straight into Impounds and Completed jobs (no dispatch job).
            No → creates an impound job in the dispatch queue.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-2">
          {/* Police paperwork identifiers */}
          <div><Label className="uppercase-label">Impound Notice Number</Label><Input value={form.impound_notice_number} onChange={(e)=>upd("impound_notice_number", e.target.value)} placeholder="e.g. IN123456" data-testid="imp-input-notice"/></div>
          <div><Label className="uppercase-label">Tow Authority Number</Label><Input value={form.tow_authority_number} onChange={(e)=>upd("tow_authority_number", e.target.value)} placeholder="e.g. TA0042" data-testid="imp-input-tow-auth"/></div>

          <div className="col-span-2 pt-2">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700 pb-1 mb-1">Owner</div>
          </div>
          <div className="col-span-2"><Label className="uppercase-label">Owner Full Name *</Label><Input value={form.customer_name} onChange={(e)=>upd("customer_name", e.target.value)} placeholder="Full legal name" data-testid="imp-input-name"/></div>
          <div className="col-span-2">
            <Label className="uppercase-label">Owner Residential Address</Label>
            <AddressAutocomplete
              value={form.impound_owner_address}
              onChange={(v) => upd("impound_owner_address", v)}
              onPick={({ label }) => upd("impound_owner_address", label)}
              placeholder="Start typing the owner's home address…"
              testIdPrefix="imp-input-owner-address"
            />
            <div className="text-[10px] text-slate-400 mt-1">Used on the owner-notice letter. Suggests as you type.</div>
          </div>
          <div className="col-span-2"><Label className="uppercase-label">Telephone Number</Label><Input value={form.customer_phone} onChange={(e)=>upd("customer_phone", e.target.value)} placeholder="021…" data-testid="imp-input-phone"/></div>

          {/* Vehicle block */}
          <div className="col-span-2 pt-2">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700 pb-1 mb-1">Vehicle</div>
          </div>
          <div><Label className="uppercase-label">Make &amp; Model *</Label><Input value={form.vehicle} onChange={(e)=>upd("vehicle", e.target.value)} placeholder="2018 Honda Civic" data-testid="imp-input-vehicle"/></div>
          <div><Label className="uppercase-label">Registration Number</Label><Input value={form.rego} onChange={(e)=>upd("rego", e.target.value.toUpperCase())} placeholder="ABC123" className="font-mono uppercase" data-testid="imp-input-rego"/></div>

          <div>
            <Label className="uppercase-label">Vehicle Weight (GVM) *</Label>
            <Select value={form.gvm_kg ? String(form.gvm_kg) : ""} onValueChange={(v)=>upd("gvm_kg", v)}>
              <SelectTrigger data-testid="imp-input-gvm"><SelectValue placeholder="Select…"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="1800">&lt; 3500 kg (Light)</SelectItem>
                <SelectItem value="5000">&gt; 3500 kg (Heavy)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="uppercase-label">Impound Term *</Label>
            <Select value={form.impound_term} onValueChange={(v)=>upd("impound_term", v)}>
              <SelectTrigger data-testid="imp-input-term"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="28d">28 days</SelectItem>
                <SelectItem value="6mo">6 months</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Impound details */}
          <div className="col-span-2 pt-2">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700 pb-1 mb-1">Impound</div>
          </div>
          <div className="col-span-2"><Label className="uppercase-label">Date &amp; Time of Seizure (24h) *</Label><DateTime24 value={form.impounded_at} onChange={(v)=>upd("impounded_at", v)} dataTestId="imp-input-when"/></div>

          <div className="col-span-2">
            <Label className="uppercase-label">Public Holiday Override</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="checkbox" id="imp-ph" checked={!!form.is_public_holiday} onChange={(e)=>upd("is_public_holiday", e.target.checked)} data-testid="imp-input-ph"/>
              <Label htmlFor="imp-ph" className="text-xs text-slate-600 dark:text-slate-300">Force public-holiday rate (otherwise auto-detected from NZ calendar).</Label>
            </div>
          </div>

          <div className="col-span-2">
            <Label className="uppercase-label">Impound Location *</Label>
            <AddressAutocomplete value={form.address}
              onChange={(v)=>setForm((f)=>({...f, address: v, lat: null, lng: null}))}
              onPick={({label,lat,lng})=>setForm((f)=>({...f, address: label, lat, lng}))}
              placeholder="Where the vehicle was seized" testIdPrefix="imp-input-pickup"/>
          </div>
          <div className="col-span-2">
            <Label className="uppercase-label">Drop-off (impound yard) *</Label>
            <Select value={form.drop_off_address} onValueChange={(v)=>setForm((f)=>({...f, drop_off_address: v, drop_off_lat: null, drop_off_lng: null}))}>
              <SelectTrigger data-testid="imp-input-dropoff"><SelectValue placeholder={yards.length ? "Select yard…" : "No yards configured — admin must set under Integrations"}/></SelectTrigger>
              <SelectContent>
                {yards.map((y, idx) => (
                  <SelectItem key={`yard-${idx}`} value={y.address}>{y.name || y.address}{y.name && y.name !== y.address ? ` · ${y.address}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-2"><Label className="uppercase-label">Notes</Label><Textarea rows={2} value={form.notes} onChange={(e)=>upd("notes", e.target.value)} data-testid="imp-input-notes"/></div>
        </div>

        {/* Live quote panel */}
        {quote && (
          <div className="mt-4 rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 p-3" data-testid="imp-live-quote">
            <div className="text-[10px] uppercase tracking-wider font-bold text-blue-700 dark:text-blue-300 mb-2">Live Quote</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <div className="text-slate-600 dark:text-slate-300">{quote.explanations.tow_fee}</div>
              <div className="text-right font-mono font-semibold text-slate-900 dark:text-slate-100">{fmtMoney(quote.line_items.tow_fee)}</div>
              <div className="text-slate-600 dark:text-slate-300">{quote.explanations.impound_fee}</div>
              <div className="text-right font-mono font-semibold text-slate-900 dark:text-slate-100">{fmtMoney(quote.line_items.impound_fee)}</div>
            </div>
            <div className="mt-2 pt-2 border-t border-blue-200 dark:border-blue-800 flex items-center justify-between">
              <div className="text-xs text-slate-500 dark:text-slate-400">Subtotal (excl. km + overstay)</div>
              <div className="font-mono font-bold text-base text-blue-900 dark:text-blue-100">{fmtMoney(quote.total)}</div>
            </div>
            <div className="mt-1 text-[10px] text-slate-500 dark:text-slate-400">
              Release due: {new Date(quote.release_due_at).toLocaleString(tenantLocale(), { timeZone: tenantTz(), dateStyle: "medium", timeStyle: "short", hourCycle: "h23" })}
              {quote.is_public_holiday && " · Public holiday rate applied"}
            </div>
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={()=>onOpenChange(false)} data-testid="imp-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white" data-testid="imp-submit">
            {saving ? "Creating…" : "Create Impound Job"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
